// auth.component.ts
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { AuthenticationService } from '../../_services/authentication.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.css']
})
export class AuthComponent implements OnInit {
  authForm: FormGroup;

  isLogin = true;
  userType: 'customer' | 'provider' = 'customer';
  isLoading = false;
  showPassword = false;
  loginError: string | null = null;
  // Where to go after auth (set by AuthRoleGuard / plan checkout); only same-app paths.
  returnUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private authenticationService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.authForm = this.createForm();
  }

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    const returnUrl = params.get('returnUrl');
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      this.returnUrl = returnUrl;
    }
    if (params.get('mode') === 'signup') {
      this.isLogin = false;
    }
    if (params.get('role') === 'provider') {
      this.userType = 'provider';
    }
    this.updateFormValidation();
  }

  get isPlanCheckout(): boolean {
    return !!this.returnUrl && this.returnUrl.includes('/checkout');
  }

  private createForm(): FormGroup {
    return this.fb.group({
      username: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      phone: ['', [Validators.required, Validators.pattern(/^\+?[\d\s-()]+$/)]],
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      address: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  get authTitle(): string {
    return this.isLogin ? 'Welcome Back' : 'Create an Account';
  }

  get authSubtitle(): string {
    return this.isLogin
      ? (this.isPlanCheckout ? 'Sign in to continue to checkout' : 'Sign in to your Already Done account')
      : this.isPlanCheckout
        ? 'Create your provider account — then start your 14-day free Pro trial'
        : 'Connect with trusted service providers in your area';
  }

  get toggleText(): string {
    return this.isLogin
      ? "Don't have an account? Sign up"
      : 'Already have an account? Sign in';
  }

  get submitButtonText(): string {
    if (this.isLoading) return 'Please wait...';
    return this.isLogin ? 'Sign In' : 'Create Account';
  }

  get shouldShowAddressField(): boolean {
    return !this.isLogin && this.userType === 'customer';
  }

  toggleAuthMode(): void {
    this.isLogin = !this.isLogin;
    this.updateFormValidation();
  }

  setUserType(type: 'customer' | 'provider'): void {
    this.userType = type;
    this.updateFormValidation();
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  private updateFormValidation(): void {
    const nameControl = this.authForm.get('name');
    const phoneControl = this.authForm.get('phone');
    const addressControl = this.authForm.get('address');

    if (this.isLogin) {
      nameControl?.clearValidators();
      phoneControl?.clearValidators();
      addressControl?.clearValidators();
    } else {
      nameControl?.setValidators([Validators.required, Validators.minLength(2)]);
      phoneControl?.setValidators([Validators.required, Validators.pattern(/^\+?[\d\s-()]+$/)]);
      if (this.userType === 'customer') {
        addressControl?.setValidators([Validators.required]);
      } else {
        addressControl?.clearValidators();
      }
    }

    nameControl?.updateValueAndValidity();
    phoneControl?.updateValueAndValidity();
    addressControl?.updateValueAndValidity();
  }

  getFieldError(fieldName: string): string {
    const control = this.authForm.get(fieldName);
    if (control?.errors && control.touched) {
      if (control.errors['required']) return `${fieldName} is required`;
      if (control.errors['email']) return 'Please enter a valid email';
      if (control.errors['minlength']) return `${fieldName} is too short`;
      if (control.errors['pattern']) return 'Please enter a valid phone number';
    }
    return '';
  }

  async onSubmit(): Promise<void> {
    // Sign-up previously posted even with empty/invalid fields; block it client-side.
    if (!this.isLogin && this.authForm.invalid) {
      this.markFormGroupTouched();
      this.loginError = 'Please fill in all required fields.';
      return;
    }
    this.isLoading = true;
    try {
      const formData = this.authForm.value;
      if (this.isLogin) {
        await this.login(formData.username, formData.password);
      } else {
        await this.register(formData);
      }
    } catch (error) {
      console.error('Authentication error:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private navigateByRole(user: any): void {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    } else if (user?.role === 'provider') {
      this.router.navigate(['/provider-dashboard']);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  private async login(username: string, password: string): Promise<void> {
    this.loginError = null;
    this.authenticationService.login(username, password).subscribe({
      next: (response) => {
        sessionStorage.setItem('currentUser', JSON.stringify(response));
        this.authService.setCurrentUser(response);
        this.navigateByRole(response);
      },
      error: (err) => {
        this.loginError = err?.error?.message || 'Invalid username or password. Please try again.';
      },
    });
  }

  private async register(userData: any): Promise<void> {
    this.loginError = null;
    const formValue = this.authForm.getRawValue();
    formValue.role = this.userType;

    this.authService.createUser(formValue).subscribe({
      next: (response) => {
        // Merge: formValue supplies role as fallback; API response wins on everything else
        const created = { role: this.userType, ...(response || {}) };
        this.authService.setCurrentUser(created);
        this.navigateByRole(created);
      },
      error: (err) => {
        console.error('Error creating user', err);
        this.loginError = err?.error?.message || 'Registration failed. Please check your details and try again.';
      }
    });
  }

  signInWithGoogle(): void {
    if (!window.google?.accounts?.oauth2) {
      this.loginError = 'Google Sign-In is not ready. Please refresh and try again.';
      return;
    }
    this.isLoading = true;
    this.loginError = null;
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: environment.googleClientId,
      scope: 'openid email profile',
      callback: (tokenResponse) => {
        if (tokenResponse.error || !tokenResponse.access_token) {
          this.loginError = 'Google sign-in was cancelled or failed.';
          this.isLoading = false;
          return;
        }
        this.handleSocialToken('google', tokenResponse.access_token, null, null);
      },
    });
    client.requestAccessToken();
  }

  async signInWithApple(): Promise<void> {
    if (!window.AppleID) {
      this.loginError = 'Apple Sign-In is not ready. Please refresh and try again.';
      return;
    }
    this.isLoading = true;
    this.loginError = null;
    try {
      window.AppleID.auth.init({
        clientId: environment.appleClientId,
        scope: 'name email',
        redirectURI: window.location.origin,
        usePopup: true,
      });
      const data = await window.AppleID.auth.signIn();
      const idToken = data.authorization?.id_token;
      const firstName = data.user?.name?.firstName ?? null;
      const lastName = data.user?.name?.lastName ?? null;
      this.handleSocialToken('apple', idToken, firstName, lastName);
    } catch (err: any) {
      if (err?.error !== 'popup_closed_by_user') {
        this.loginError = 'Apple sign-in failed. Please try again.';
      }
      this.isLoading = false;
    }
  }

  private handleSocialToken(
    provider: 'google' | 'apple',
    token: string,
    firstName: string | null,
    lastName: string | null
  ): void {
    this.authService.socialLogin(provider, token, firstName, lastName).subscribe({
      next: (user) => {
        const created = { role: 'customer' as const, ...user };
        this.authService.setCurrentUser(created);
        sessionStorage.setItem('currentUser', JSON.stringify(created));
        this.navigateByRole(created);
        this.isLoading = false;
      },
      error: (err) => {
        this.loginError = err?.error?.message
          || `${provider === 'google' ? 'Google' : 'Apple'} sign-in failed. Please try again.`;
        this.isLoading = false;
      },
    });
  }

  private markFormGroupTouched(): void {
    Object.keys(this.authForm.controls).forEach(key => {
      this.authForm.get(key)?.markAsTouched();
    });
  }
}
